import { Link } from '@inertiajs/react'
import { Calendar, FileText } from 'lucide-react'

import { AppShell } from '@/layouts/AppShell'
import { PageHeader } from '@/components/layout/PageHeader'
import { Card } from '@/components/ui/Card'
import { Badge } from '@/components/ui/Badge'
import { EmptyState } from '@/components/ui/EmptyState'
import { ContributionGraph } from '@/pages/Player/SoccerDiaries/components/ContributionGraph'
import type { Breadcrumb } from '@/types/shared'

type TrainingContent = {
  id: string
  name: string
}

type DiaryTheme = {
  id: string
  name: string
}

type Diary = {
  id: string
  diaryDate: string
  activityType: string
  activityTypeLabel: string
  concentrationRating: number | null
  attitudeRating: number | null
  understandingRating: number | null
  challengeRating: number | null
  continuityRating: number | null
  reflection: string | null
  trainingContents: TrainingContent[]
  diaryThemes: DiaryTheme[]
  createdAt: string
}

type ContributionData = {
  date: string
  count: number
}

type Player = {
  id: string
  name: string
  teamName: string
}

type Props = {
  player: Player
  diaries: Diary[]
  contributionData: ContributionData[]
  breadcrumbs: Breadcrumb[]
}

export default function Index({ player, diaries, contributionData, breadcrumbs }: Props) {
  return (
    <AppShell>
      <PageHeader
        title={`${player.name}のサッカー日記`}
        description={player.teamName}
        breadcrumbs={breadcrumbs}
      />

      <div className="space-y-8">
        <Card className="overflow-hidden">
          <div className="mb-4">
            <h2 className="text-lg font-semibold text-white">今年の活動記録</h2>
            <p className="text-sm text-white/60">日記の継続性から選手の人間性を確認できます</p>
          </div>
          <ContributionGraph data={contributionData} />
        </Card>

        <div className="space-y-4">
          <h2 className="text-lg font-semibold text-white">日記一覧</h2>
          {diaries.length === 0 ? (
            <EmptyState
              icon={<FileText className="h-12 w-12" />}
              title="まだ日記がありません"
              description="この選手はまだサッカー日記を書いていません"
            />
          ) : (
            <div className="space-y-4">
              {diaries.map((diary) => (
                <DiaryCard key={diary.id} diary={diary} playerId={player.id} />
              ))}
            </div>
          )}
        </div>
      </div>
    </AppShell>
  )
}

function DiaryCard({ diary, playerId }: { diary: Diary; playerId: string }) {
  const date = new Date(diary.diaryDate)
  const formattedDate = `${date.getFullYear()}年${date.getMonth() + 1}月${date.getDate()}日`
  const weekday = ['日', '月', '火', '水', '木', '金', '土'][date.getDay()]

  return (
    <Link href={`/school/players/${playerId}/soccer_diaries/${diary.id}`}>
      <Card className="transition-colors hover:bg-white/10">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-4">
            <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl bg-brand-500/20 text-brand-300">
              <Calendar className="h-6 w-6" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="font-semibold text-white">
                  {formattedDate}（{weekday}）
                </h3>
                <Badge variant={activityTypeBadgeVariant(diary.activityType)}>
                  {diary.activityTypeLabel}
                </Badge>
              </div>
              {diary.trainingContents.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1">
                  {diary.trainingContents.map((tc) => (
                    <span key={tc.id} className="rounded-full bg-white/10 px-2 py-0.5 text-xs text-white/70">
                      {tc.name}
                    </span>
                  ))}
                </div>
              )}
              {diary.reflection && (
                <p className="mt-2 line-clamp-2 text-sm text-white/60">
                  {diary.reflection}
                </p>
              )}
            </div>
          </div>
          <div className="flex flex-shrink-0 items-center gap-1">
            <RatingStars rating={averageRating(diary)} />
          </div>
        </div>
      </Card>
    </Link>
  )
}

function RatingStars({ rating }: { rating: number | null }) {
  if (rating === null) return null

  const fullStars = Math.round(rating)
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => (
        <span
          key={star}
          className={star <= fullStars ? 'text-yellow-400' : 'text-white/20'}
        >
          ★
        </span>
      ))}
    </div>
  )
}

function averageRating(diary: Diary): number | null {
  const ratings = [
    diary.concentrationRating,
    diary.attitudeRating,
    diary.understandingRating,
    diary.challengeRating,
    diary.continuityRating,
  ].filter((r): r is number => r !== null)

  if (ratings.length === 0) return null
  return ratings.reduce((a, b) => a + b, 0) / ratings.length
}

function activityTypeBadgeVariant(activityType: string) {
  switch (activityType) {
    case 'practice':
      return 'info' as const
    case 'match':
      return 'success' as const
    case 'self_training':
      return 'warning' as const
    default:
      return 'outline' as const
  }
}
